import { useEffect, useRef, useState } from "react";
import Avatar from "components/avatar";
import Button from "components/button";
import { DatePicker, InputField, Select } from "components/fields";
import AtriumApi, { getApiErrorMessage } from "api";
import notify from "lib/notify";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import MapboxAddressInput from "components/mapbox";
import { MdFileUpload } from "react-icons/md";
import { SettingsSubsection } from "./components/SettingsSection";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const FAMILY_RELATIONSHIP_OPTIONS = [
  { value: "primary", label: "Primary" },
  { value: "married", label: "Married" },
  { value: "child", label: "Child" },
];

const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
];

const validationSchema = Yup.object().shape({
  status: Yup.string().required("Status is required"),
  campus: Yup.string().required("Campus is required"),
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string().email("Invalid email format").required("Email is required"),
  phone: Yup.string()
    .matches(/^[0-9\s\-()+]*$/, "Phone number should only contain numbers.")
    .required("Phone number is required"),
  familyRelationship: Yup.string().required("Family relationship is required"),
  dob: Yup.string().required("Date of birth is required"),
  gender: Yup.string().required("Gender is required"),
  address: Yup.string().required("Address is required"),
});

const toFormValues = (u) => ({
  status: u?.status || "",
  campus: u?.campus || "",
  firstName: u?.firstName || "",
  lastName: u?.lastName || "",
  email: u?.email || "",
  phone: u?.phone || "",
  dob: u?.dob || "",
  familyRelationship: u?.familyRelationship || "",
  gender: u?.gender || "",
  avatar: u?.avatar?.url || "",
  address: u?.address || "",
  city: u?.city || "",
  street: u?.street || "",
  state: u?.state || "",
  zip: u?.zip || "",
  country: u?.country || "",
  id: u?.id || "",
});

const toPoint = (u) => ({ type: "Point", coordinates: [u?.lng, u?.lat] });

// The API accepts dates as MM/DD/YYYY.
function formatToMMDDYYYY(date) {
  if (!date) return "";
  const d = new Date(date);
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const year = d.getFullYear();
  return `${month}/${day}/${year}`;
}

/**
 * Staff edit form. Rendered inside ManageStaffDrawer, whose footer submits it
 * through the `formId` attribute.
 */
const ManageStaffForm = ({ user: staffUser, formId, onClose, onSavingChange, getStaffList }) => {
  const [user, setUser] = useState(() => toFormValues(staffUser));
  const [image, setImage] = useState(staffUser?.avatar?.url || "");
  const [imageFile, setImageFile] = useState(null);
  const [location, setLocation] = useState(() => toPoint(staffUser));
  const [campuses, setCampuses] = useState([]);
  const fileInputRef = useRef(null);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(URL.createObjectURL(file));
      setImageFile(file);
    }
  };

  useEffect(() => {
    const fetchAllCampuses = async () => {
      try {
        const response = await AtriumApi.get("/v2/campuses/allCampuses");
        if (response) {
          setCampuses(response?.data?.data?.campuses?.data || []);
        }
      } catch (error) {
        console.error("Failed to load campuses:", error);
      }
    };
    fetchAllCampuses();
  }, []);

  useEffect(() => {
    if (!user.id && staffUser?.id) {
      setUser(toFormValues(staffUser));
      setImage(staffUser?.avatar?.url);
      setLocation(toPoint(staffUser));
    }
  }, [user, staffUser]);

  const onSubmit = async (values) => {
    onSavingChange?.(true);

    let requestData;
    let config = {};

    // Use FormData only when uploading a new file
    if (imageFile) {
      const formData = new FormData();
      formData.append("role", "staff");

      Object.keys(values).forEach((key) => {
        if (key !== "avatar" && key !== "role") {
          formData.append(key, values[key]);
        }
      });
      formData.append("location", JSON.stringify(location));
      formData.append("avatar", imageFile);

      requestData = formData;
      // No Content-Type header: axios sets it with the multipart boundary.
      config = {};
    } else {
      requestData = {
        ...values,
        role: "staff", // Always hardcode the role as 'staff'
        location,
      };
      delete requestData.avatar; // The backend keeps the existing avatar
    }

    try {
      const response = user?.id
        ? await AtriumApi.put(`/v1/users/${user?.id}`, requestData, config)
        : await AtriumApi.post("/v1/users", requestData, config);

      if (response.status === 422) {
        notify.error(response.data?.message || "Could not save staff member");
      } else if (response.status === 200) {
        notify.success("Staff member saved");
        onClose();
        getStaffList();
      } else {
        throw new Error("unexpected response");
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not save staff member"));
    } finally {
      onSavingChange?.(false);
    }
  };

  const displayName = `${user.firstName || ""} ${user.lastName || ""}`.trim();

  return (
    <Formik
      enableReinitialize
      initialValues={user}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => {
        const field = (name) => ({
          name,
          value: values[name] || "",
          error: errors[name],
          touched: touched[name],
          onChange: handleChange,
          onBlur: handleBlur,
        });
        return (
          <Form id={formId} noValidate>
            <SettingsSubsection title="Access">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Select
                  label="Status"
                  required
                  placeholder="Select status"
                  options={STATUS_OPTIONS}
                  {...field("status")}
                />
                <Select
                  label="Campus"
                  required
                  placeholder={campuses?.length > 0 ? "Select a campus" : "No campuses found"}
                  options={(campuses || []).map((campus) => ({
                    value: campus?.id,
                    label: campus?.name,
                  }))}
                  {...field("campus")}
                />
              </div>
            </SettingsSubsection>

            <SettingsSubsection title="User Details">
              <div className="mb-4 flex items-center gap-4">
                <Avatar src={image || undefined} name={displayName} size="lg" />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  tabIndex={-1}
                  aria-hidden="true"
                  onChange={handleImageChange}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<MdFileUpload />}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Upload Photo
                </Button>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <InputField label="First Name" required {...field("firstName")} />
                <InputField label="Last Name" required {...field("lastName")} />
                <InputField label="Email Address" required type="email" {...field("email")} />
                <InputField label="Contact Phone" required type="tel" {...field("phone")} />
                <DatePicker
                  isFutureDateDisabled
                  name="dob"
                  label="Date of Birth"
                  error={errors.dob}
                  touched={touched.dob}
                  value={formatToMMDDYYYY(values.dob)}
                  onChange={(v) => setFieldValue("dob", formatToMMDDYYYY(v))}
                  onBlur={handleBlur}
                />
                <Select
                  label="Family Relationship"
                  required
                  placeholder="Select relationship"
                  options={FAMILY_RELATIONSHIP_OPTIONS}
                  {...field("familyRelationship")}
                />
                <Select
                  label="Gender"
                  required
                  placeholder="Select gender"
                  options={GENDER_OPTIONS}
                  {...field("gender")}
                />
              </div>
            </SettingsSubsection>

            <SettingsSubsection title="Location">
              <MapboxAddressInput
                label="Address"
                id="address"
                name="address"
                required
                value={values?.address || staffUser?.address || ""}
                onResult={(data) => {
                  setLocation({
                    type: "Point",
                    coordinates: [data?.lng, data?.lat],
                  });

                  if (data) {
                    setFieldValue("address", data?.address || "");
                    setFieldValue("state", data?.state || "");
                    setFieldValue("city", data?.city || "");
                    setFieldValue("street", data?.street || data?.address || "");
                    setFieldValue("zip", data?.zipCode || "");
                  }
                }}
                onClearInput={() => {
                  setLocation({});
                  setFieldValue("address", "");
                  setFieldValue("state", "");
                  setFieldValue("city", "");
                  setFieldValue("street", "");
                  setFieldValue("zip", "");
                }}
              />
              {!values.address && touched.address && errors.address && (
                <p className="mt-1 text-xs text-danger-fg">{errors.address}</p>
              )}
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <InputField label="State" {...field("state")} />
                <InputField label="City" {...field("city")} />
                <InputField label="Street Address" {...field("street")} />
                <InputField label="Zip Code" {...field("zip")} />
              </div>
            </SettingsSubsection>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ManageStaffForm;
